<template>
    <div class="campaign--budget-weekly">
        <div class="row campaign-adset--budget-interval">
            <div class="col-md-4">
                <div class="campaign-option campaign-adset--budget-selection">
                    <div class="campaign-option--subtitle">
                        Budget Type
                        <info-tooltip text="Daily: This allows you to set a daily spend allocation.<br> Weekly: This allows you to set spend allocations for specific days of the week.<br> Open: No spending limits will be applied for this ad set."></info-tooltip>
                    </div>
                    <multiselect v-model="budgetInterval"
                                 :options="filteredDimensions"
                                 label="label"
                                 track-by="id"
                                 :show-labels="false"
                                 :searchable="false"
                                 :allow-empty="false">
                    </multiselect>
                    <slot name="budgetTypeError"></slot>
                </div>
            </div>
            <div class="col-md-4" v-show="isSingleInput">
                <div class="campaign-option campaign-adset--budget-amount">
                    <div class="campaign-option--title">Budget Amount</div>
                    <currency-input v-model="budgets[0].budget" :class-name="'text-input'"></currency-input>
                    <slot name="budgetAmountError"></slot>
                </div>
            </div>
            <div class="col-md-4" v-show="isWeekly">
                <div class="campaign-option campaign-adset--budget-amount">
                    <div class="campaign-option--title">Budget Total</div>
                    <span class="campaign-adset--budget-amount__total">${{ formatNumber(weeklyTotal) }}.00</span>
                    <slot name="budgetAmountErrorWeekly"></slot>
                </div>
            </div>
        </div>

        <div class="row campaign-adset--budget-weekly" v-show="isWeekly">
            <div class="col-md-12 budget-weekly-bar-charts">
                <div class="col-md-1" v-for="(interval, index) in budgets">
                    <div><div :style="styles[index]" class="bar-chart"></div></div>
                    <label>{{ daysOfWeek[index] }}</label>
                    <currency-input class-name="text-input" v-model="budgets[index].budget" ></currency-input>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
    import Multiselect from 'vue-multiselect'

    export default {
        props: ['value', 'dimensionsBudgetIntervals', 'productCategoryId'],

        data() {
            return {
                budgets: this.value,
                budgetInterval: {id: null, label: "None"},
                daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
                styles: Array(7).fill({}),
            };
        },

        mounted() {
            this.updateBudgetIntervals(this.value);
        },

        computed: {
            budgetIntervalId() {
                return String(this.budgetInterval.id);
            },

            intervalType() {
                switch (this.budgetIntervalId) {
                    case '1':
                    case '2':
                    case '3':
                    case '4':
                    case '5':
                    case '6':
                    case '7':
                        return 'weekly';
                    case '8':
                        return 'daily';
                    case '10':
                        return 'fixed';
                    case '9':
                    default:
                        return 'open';
                }
            },

            isWeekly() {
                return this.intervalType === 'weekly';
            },

            isSingleInput() {
                return !this.isWeekly && this.budgetIntervalId != 0 && this.budgetIntervalId != 9;
            },

            weeklyTotal() {
                return this.budgets.reduce((total, value) => total + value.budget, 0);
            },

            budgetAmount() {
                return this.isSingleInput ? this.budgets[0].budget || 0 : 0;
            },

            filteredDimensions() {
                let newDimensionsBudgetIntervals = [...this.dimensionsBudgetIntervals]
                newDimensionsBudgetIntervals.splice(0, 7, {id:1, label:"Weekly"});

                if (this.budgetInterval.id === null) {
                    return newDimensionsBudgetIntervals;
                }

                if (this.productCategoryId === 24 && Number(this.budgetInterval.id) !== 1) {
                    newDimensionsBudgetIntervals.splice(0, 1);
                }

                return newDimensionsBudgetIntervals;
            },

            coalescedChanges() {
                this.weeklyTotal;
                this.budgetInterval;
                this.budgetAmount;
                return String(this.weeklyTotal) + String(this.budgetInterval.id) + String(this.budgetAmount);
            }
        },

        watch: {
            value(val) {
                this.updateBudgetIntervals(val);
            },

            budgetIntervalId(newValue, oldValue) {
                if (newValue == "1" && newValue != oldValue && this.budgets.length != 7) {
                    this.budgets = Array(7).fill({}).map(function (interval, index) {
                        return { budget: 0, budget_interval: String(index+1) };
                    });
                }
            },

            coalescedChanges() {
                this.updateBudgets();
            },

            dimensionsBudgetIntervals() {
                this.updateBudgetIntervals(this.value);
            }
        },

        methods: {
            updateBudgets() {
                switch(this.budgetIntervalId) {
                    case '1':
                        this.updateBarCharts();
                        break;
                    case '8':
                    case '10':
                        this.budgets = [{"budget": this.budgetAmount, "budget_interval": this.budgetIntervalId }];
                        break;
                    case '9':
                        this.budgets = [{"budget": 0, "budget_interval": this.budgetIntervalId }];
                        break;
                }
                //send the budgets object up to parent
                this.$emit('input', this.budgets)
            },

            updateBarCharts() {
                // scale and color the bar charts
                let heightMax = 50;
                let max = Math.max(... this.budgets.map(interval => interval.budget)) + 1;
                let scale = heightMax / max;

                let self = this;
                this.daysOfWeek.forEach(function (day, index) {
                    let dayBudget = self.budgets[index].budget;
                    let height = dayBudget * scale;

                    self.styles[index] = {
                        'height': height != 0 ? height + 'px' : '0',
                        'background-color': '#b2cbf7'
                    };
                });
            },

            formatNumber: function (num) {
                num = (num + '').replace(/[^0-9+\-Ee.]/g, '');
                return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ","); //thousands separator
            },

            updateBudgetIntervals(value) {
                this.budgets = value;
                this.budget_interval = value[0].budget_interval;

                let budgetInterval = _.find(this.filteredDimensions, {'id': Number(this.budget_interval)});
                if (budgetInterval) {
                    this.budgetInterval = {id: this.budget_interval, label: budgetInterval.label};
                } else {
                    this.budgetInterval = {id: 9, label: "Open"};
                }
            }
        },
        components: {
            Multiselect
        }
    }
</script>

<style>
    .budget-weekly-bar-charts {
        padding-top: 10px;
    }

    /*days of week div*/
    .budget-weekly-bar-charts > div {
        text-align: center;
        min-width: 112px;
        padding-right: 0;
    }

    .budget-weekly-bar-charts > div:first-child {
        padding-left: 0;
        min-width: 100px;
    }

    /*container for bar*/
    .budget-weekly-bar-charts > div > div {
        position: relative;
        height: 51px;
    }

    /*bar itself*/
    .budget-weekly-bar-charts > div > div > div {
        border-bottom: 1px solid #787878;
        position: absolute;
        bottom: 0;
        width: 100%;
    }

    /*bar currency size*/
    .budget-weekly-bar-charts .text-input {
        width: 100px;
    }

    .budget-weekly-bar-charts .bar-chart {
        width: 100px;
    }

    .budget-weekly-bar-charts > div > label {
        font-size: 13px;
        padding-left: 1px;
        margin-bottom: 3px;
        text-align: left;
        width: 100%;
    }
</style>
